import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/login.css";
import logo from "../assets/primerise-logo.png";

const API_BASE_URL = import.meta.env.VITE_API_BASE || "https://primerise.space";

const Login = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const wait = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const fetchWithTimeout = async (url, options = {}, timeout = 20000) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const checkServerHealth = async () => {
    try {
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/health`,
        {
          method: "GET",
        },
        12000
      );

      return response.ok;
    } catch (error) {
      console.error("Server health check failed:", error);
      return false;
    }
  };

  const parseResponseJson = async (response) => {
    try {
      return await response.json();
    } catch (error) {
      console.error("Response JSON parse error:", error);
      return {};
    }
  };

  const loginFromApi = async (email, password) => {
    const endpoint = `${API_BASE_URL}/api/auth/login`;
    let lastError = null;

    for (let attempt = 1; attempt <= 3; attempt += 1) {
      try {
        const response = await fetchWithTimeout(
          endpoint,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, password }),
          },
          25000
        );

        const data = await parseResponseJson(response);

        if (!response.ok) {
          throw new Error(data.message || "Login failed");
        }

        const userData = data.user || data.data || data;
        const token = data.token || data.accessToken || "";

        localStorage.setItem("primeriseAuth", "true");
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("primeriseCurrentUser", JSON.stringify(userData));
        localStorage.setItem("currentUser", JSON.stringify(userData));

        // Old keys also kept for copied Naxora auth guards
        localStorage.setItem("naxoraAuth", "true");
        localStorage.setItem("naxoraCurrentUser", JSON.stringify(userData));

        if (token) {
          localStorage.setItem("token", token);
          localStorage.setItem("primeriseToken", token);

          // Old key kept for copied code compatibility
          localStorage.setItem("naxoraToken", token);
        }

        return true;
      } catch (error) {
        lastError = error;
        console.error(`Login attempt ${attempt} failed:`, error);

        if (attempt < 3) {
          await wait(1200);
        }
      }
    }

    const serverHealthy = await checkServerHealth();

    if (!serverHealthy) {
      throw new Error(
        "Server is taking longer than usual. Please try again in a few moments."
      );
    }

    if (lastError?.name === "AbortError") {
      throw new Error(
        "Login request timed out. Please check your internet connection and try again."
      );
    }

    if (
      String(lastError?.message || "")
        .toLowerCase()
        .includes("failed to fetch")
    ) {
      throw new Error(
        "Connection issue detected. Please check your internet connection or try again after a moment."
      );
    }

    throw new Error(lastError?.message || "Login failed. Please try again.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const enteredEmail = formData.email.trim().toLowerCase();
    const enteredPassword = formData.password.trim();

    if (!enteredEmail || !enteredPassword) {
      alert("Please enter both email and password.");
      return;
    }

    setLoading(true);

    try {
      await loginFromApi(enteredEmail, enteredPassword);
      navigate("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      alert(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-brand">
          <img src={logo} alt="Primerise Logo" className="login-logo" />
        </div>

        <div className="login-card">
          <div className="login-card-top">
            <h2>Welcome Back</h2>
            <p>Login to your secure account</p>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
              />
            </div>

            <div className="input-group">
              <label>Password</label>

              <div className="password-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                />

                <button
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="login-options">
              <label className="remember-me">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>

              <Link to="/forgot-password" className="forgot-link">
                Forgot Password?
              </Link>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? "Signing In..." : "Login"}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Don&apos;t have an account?{" "}
              <Link to="/register" className="register-link">
                Register
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;